import React, { useState, useEffect, useRef } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

export function isTurnstileEnabled() {
  return Boolean(TURNSTILE_SITE_KEY)
}

function getTurnstileTheme() {
  if (typeof document === 'undefined') return 'auto'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'auto'
}

/**
 * Cloudflare Turnstile widget. Renders nothing when site key is not configured.
 * Requires hostname in Cloudflare Turnstile widget settings (e.g. trophytime.pages.dev).
 */
export function TurnstileWidget({ onToken, onExpire, onError, className = '' }) {
  const turnstileRef = useRef(null)
  const [theme, setTheme] = useState(getTurnstileTheme)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    setTheme(getTurnstileTheme())
    const observer = new MutationObserver(() => setTheme(getTurnstileTheme()))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  if (!isTurnstileEnabled()) return null

  const handleError = () => {
    setLoadError(true)
    onToken?.('')
    onError?.()
  }

  const handleRetry = () => {
    setLoadError(false)
    onToken?.('')
    turnstileRef.current?.reset()
  }

  if (loadError) {
    return (
      <div
        className={`rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 ${className}`}
        role="alert"
      >
        <p className="font-semibold">Security check couldn't load</p>
        <p className="mt-1 text-xs leading-relaxed opacity-90">
          Please check your connection and try again.
        </p>
        <button
          type="button"
          onClick={handleRetry}
          className="mt-3 rounded-md bg-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-300 dark:bg-amber-800 dark:text-amber-50"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className={`flex min-h-[65px] justify-center ${className}`}>
      <Turnstile
        ref={turnstileRef}
        siteKey={TURNSTILE_SITE_KEY}
        options={{
          theme,
          size: 'normal',
          retry: 'auto',
          refreshExpired: 'auto',
        }}
        onSuccess={(token) => {
          setLoadError(false)
          onToken?.(token)
        }}
        onExpire={() => {
          onToken?.('')
          onExpire?.()
        }}
        onError={handleError}
      />
    </div>
  )
}

export function requireCaptchaToken(captchaToken) {
  if (!isTurnstileEnabled()) return
  if (!captchaToken) {
    throw new Error('Please complete the security check before continuing.')
  }
}
